import React, { useState, useEffect, useRef, useReducer, useContext } from 'react';
import { MainContext } from '@blaze-cms/nextjs-components';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { parseUrl } from 'query-string';
import SearchFilter from './SearchFilter';
import { withTitle } from '../../HOC';
import { getSearchPublishedContent, generateSingleItemQuery } from '../../application/query';
import {
  buildNewUrl,
  buildRawQueryStringified,
  getFilterValues,
  buildFiltersQuery,
  getFilterRangesAndCheckboxesValues,
  getUpdatedFilterBy
} from './helpers';
import { getEntityData, getInheritedFilters, getQueryProps } from '../../helpers';
import { RAW_RESULTS } from './constants';
import { SCROLL_OFFSET } from '../../constants';
import searchFilterReducer from './searchFilterReducer';
import { useGetEntitySchemasAsObj } from '../../hooks';

const getFiltersUrlQuery = (query, listName) => {
  if (!listName) return query;
  const updatedQuery = {};
  const listKey = `pb[${listName}]`;
  Object.keys(query).forEach(queryKey => {
    const isListKey = queryKey.indexOf(`${listName}`) !== -1;
    if (!isListKey) return;
    const decodedQueryValue = Array.isArray(query[queryKey])
      ? query[queryKey].map(value => decodeURIComponent(value))
      : decodeURIComponent(query[queryKey]);
    const strippedKey = queryKey.substring(listKey.length + 1, queryKey.length - 1);
    if (isListKey) updatedQuery[strippedKey] = decodedQueryValue;
  });

  return updatedQuery;
};

const SearchFilterContainer = ({
  entity,
  url,
  filters,
  name,
  isCollapsedOnResponsive,
  groupAfterMobile,
  groupAfterDesktop,
  filterBy,
  filterByProperty,
  shouldAddFilters,
  listComponentName,
  parent
}) => {
  const { isPreview } = useContext(MainContext);
  const router = useRouter();
  const { asPath } = router;
  const searchFilterRef = useRef(null);
  const [key, setKey] = useState(`filter-${name}`);
  const [displaySearchFilter, setDisplaySearchFilter] = useState(false);
  const [urlPath, setUrlPath] = useState(asPath); // used as asPath can take too long to update
  const [filterValues, dispatch] = useReducer(searchFilterReducer, null);
  const { url: currentUrl, query } = parseUrl(urlPath);
  const updatedQuery = getFiltersUrlQuery(query, listComponentName);
  const { itemId: parentId, itemEntity: parentEntity } = parent;

  useEffect(
    () => {
      if (asPath) setUrlPath(asPath);
    },
    [asPath]
  );
  const { data: mainSchemas = {}, loading: schemasLoading } = useGetEntitySchemasAsObj([
    parentEntity,
    entity
  ]);
  const { [parentEntity]: parentSchema = {}, [entity]: filterEntitySchema = {} } = mainSchemas;
  const { actions = {} } = parentSchema || {};
  const getAction = isPreview ? actions.get : actions.getPublished;
  const inheritedFilters = getInheritedFilters(filterBy, filterByProperty);
  const queryProps = getQueryProps(inheritedFilters, parentSchema, filterEntitySchema);
  const { data: { entityData: parentData } = {}, loading: parentQueryLoading } = useQuery(
    generateSingleItemQuery(getAction, queryProps),
    {
      variables: { id: parentId },
      skip: schemasLoading || !inheritedFilters.length
    }
  );
  const { updatedFilterBy, updatedFilterByProperty } = getUpdatedFilterBy(
    filterBy,
    filterByProperty,
    parentData
  );

  const action = getSearchPublishedContent(RAW_RESULTS);
  const [checkboxSelectValues, rangeValues] = getFilterRangesAndCheckboxesValues(filters);

  const { docType } = getEntityData(entity);

  const filtersQuery = buildFiltersQuery({
    shouldAddFilters,
    query: filterValues,
    filterBy: updatedFilterBy,
    filterByProperty: updatedFilterByProperty,
    rangeValues,
    queryKeys: Object.keys(updatedQuery)
  });

  const rawQueryStringified = buildRawQueryStringified(
    checkboxSelectValues,
    rangeValues,
    docType,
    filtersQuery
  );

  const { data, error, loading } = useQuery(action, {
    variables: { rawQueryStringified, limit: 0 }, // we only want aggs so limit=0 for no search results
    skip: !rawQueryStringified || parentQueryLoading
  });

  if (error) return error.message;
  if (!filters.length) return null;

  const { searchPublishedContent: { rawResults: { aggregations: filterData } = {} } = {} } =
    data || {};

  if (!filterValues && !loading && !parentQueryLoading && (filterData || !rawQueryStringified)) {
    const newValues = getFilterValues(filterData, filters, updatedQuery);
    dispatch({ newValues, shouldSearch: false, type: 'update' });
    return null;
  }

  const handleSearch = newQuery => {
    setDisplaySearchFilter(false);
    const hashBit = asPath.split('#')[1] || '';
    const parsedHashBit = hashBit ? `#${hashBit}` : '';
    scrollToFirstList();
    if (!newQuery) {
      const baseQuery = `${currentUrl}${parsedHashBit}`;
      setUrlPath(baseQuery);
      return router.push('/Resolver', baseQuery, { shallow: !url, scroll: false }).then(() => {
        setKey(`filter-${name}:${Date.now()}`); // remove after range component update
      });
    }
    const newUrl = buildNewUrl(url, currentUrl, newQuery, hashBit);
    setUrlPath(newUrl);
    return router.push('/Resolver', newUrl, { shallow: !url, scroll: false });
  };

  const scrollToFirstList = () => {
    const [list] = document.getElementsByClassName('list-top');
    const shouldScrollToFirstList = !url && list;

    if (shouldScrollToFirstList) {
      window.scrollTo({
        left: 0,
        top: window.pageYOffset - SCROLL_OFFSET + list.getBoundingClientRect().top
      });
    } else {
      window.scrollTo(0, 0);
    }
  };

  if (!filterValues) return null;

  return (
    <SearchFilter
      key={key}
      setAppliedFilters={dispatch}
      query={updatedQuery}
      listComponentName={listComponentName}
      searchFilterRef={searchFilterRef}
      data={filterData}
      filters={filters}
      hasUrl={!!url}
      entity={entity}
      handleSearch={handleSearch}
      filterValues={filterValues}
      name={name}
      isCollapsedOnResponsive={isCollapsedOnResponsive}
      displaySearchFilter={displaySearchFilter}
      setDisplaySearchFilter={setDisplaySearchFilter}
      groupAfterMobile={groupAfterMobile}
      groupAfterDesktop={groupAfterDesktop}
    />
  );
};

SearchFilterContainer.propTypes = {
  entity: PropTypes.string,
  url: PropTypes.string,
  filters: PropTypes.array,
  name: PropTypes.string.isRequired,
  isCollapsedOnResponsive: PropTypes.bool,
  groupAfterMobile: PropTypes.number,
  groupAfterDesktop: PropTypes.number,
  filterBy: PropTypes.array,
  filterByProperty: PropTypes.array,
  shouldAddFilters: PropTypes.bool,
  listComponentName: PropTypes.string,
  parent: PropTypes.object
};

SearchFilterContainer.defaultProps = {
  url: null,
  filters: [],
  entity: '',
  isCollapsedOnResponsive: true,
  groupAfterMobile: 0,
  groupAfterDesktop: 0,
  filterBy: [],
  filterByProperty: [],
  shouldAddFilters: false,
  listComponentName: '',
  parent: {}
};

export default withTitle(SearchFilterContainer);
